// Synthèse de sons « type piano » pour les tests automatisés et la source de synthèse
// de l'application (vérification de la chaîne audio sans micro).
// Ce n'est pas un vrai piano : partiels légèrement inharmoniques, attaque brève,
// décroissance plus rapide pour les partiels aigus.

export interface ToneOptions {
  /** Amplitudes relatives des partiels 1..K. */
  harmonics?: number[];
  /** Décroissance de base en 1/s (le partiel k décroît en decay × (1 + 0,6 (k−1))). */
  decay?: number;
  attackMs?: number;
  /** Coefficient d'inharmonicité B (f_k = k f √(1 + B k²)). */
  inharmonicity?: number;
  /** Amplitude crête, 0 à 1. */
  amplitude?: number;
  /** Phases initiales fixes pour la reproductibilité. */
  seed?: number;
}

export const PIANO_LIKE_HARMONICS = [1, 0.6, 0.4, 0.25, 0.15, 0.1];

export function synthesizeTone(frequency: number, durationSec: number, sampleRate: number, options: ToneOptions = {}): Float32Array {
  const harmonics = options.harmonics ?? PIANO_LIKE_HARMONICS;
  const decay = options.decay ?? 2.5;
  const attack = ((options.attackMs ?? 5) / 1000) * sampleRate;
  const B = options.inharmonicity ?? 0.0003;
  const amplitude = options.amplitude ?? 0.5;
  const n = Math.floor(durationSec * sampleRate);
  const out = new Float32Array(n);
  let norm = 0;
  for (const h of harmonics) norm += h;
  let seed = options.seed ?? 1;
  const phases = harmonics.map(() => {
    seed = (seed * 16807) % 2147483647;
    return (seed / 2147483647) * 2 * Math.PI;
  });
  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    let s = 0;
    for (let k = 1; k <= harmonics.length; k++) {
      const fk = k * frequency * Math.sqrt(1 + B * k * k);
      if (fk >= sampleRate / 2) break;
      const env = Math.exp(-t * decay * (1 + 0.6 * (k - 1)));
      s += harmonics[k - 1] * env * Math.sin(2 * Math.PI * fk * t + phases[k - 1]);
    }
    const a = i < attack ? i / attack : 1;
    out[i] = (amplitude * a * s) / norm;
  }
  return out;
}

export function silence(durationSec: number, sampleRate: number): Float32Array {
  return new Float32Array(Math.floor(durationSec * sampleRate));
}

export function concat(...parts: Float32Array[]): Float32Array {
  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Float32Array(total);
  let pos = 0;
  for (const p of parts) {
    out.set(p, pos);
    pos += p.length;
  }
  return out;
}

/** Additionne `b` dans `a` à partir de l'échantillon `offset` (agrandit si nécessaire). */
export function mixInto(a: Float32Array, b: Float32Array, offset: number): Float32Array {
  const length = Math.max(a.length, offset + b.length);
  const out = new Float32Array(length);
  out.set(a, 0);
  for (let i = 0; i < b.length; i++) out[offset + i] += b[i];
  return out;
}

/** Bruit blanc déterministe (générateur congruentiel), amplitude crête donnée. */
export function whiteNoise(durationSec: number, sampleRate: number, amplitude: number, seed = 42): Float32Array {
  const n = Math.floor(durationSec * sampleRate);
  const out = new Float32Array(n);
  let s = seed;
  for (let i = 0; i < n; i++) {
    s = (s * 1664525 + 1013904223) % 4294967296;
    out[i] = ((s / 4294967296) * 2 - 1) * amplitude;
  }
  return out;
}

export function addNoise(signal: Float32Array, amplitude: number, seed = 7): Float32Array {
  const noise = whiteNoise(signal.length / 1, 1, amplitude, seed);
  const out = new Float32Array(signal.length);
  for (let i = 0; i < signal.length; i++) out[i] = signal[i] + noise[i];
  return out;
}
